/** Marketing workspace role matrix: View / Actions / Control, nine compact rows. */

export type MarketingWorkspaceRole =
  | "Administrator"
  | "Producer"
  | "Editor"
  | "Viewer";

export const RBAC_ROLES: MarketingWorkspaceRole[] = [
  "Administrator",
  "Producer",
  "Editor",
  "Viewer",
];

export const RBAC_ACTION_COLUMNS = ["View", "Actions", "Control"] as const;

export type RbacActionColumn = (typeof RBAC_ACTION_COLUMNS)[number];

const P = {
  WORKSPACE_READ: "workspace:read",
  WORKSPACE_UPDATE: "workspace:update",
  MEMBER_LIST: "workspace:member:list",
  MEMBER_INVITE: "workspace:member:invite",
  MEMBER_UPDATE: "workspace:member:update",
  MEMBER_REMOVE: "workspace:member:remove",
  BILLING_READ: "workspace:billing:read",
  BILLING_UPDATE: "workspace:billing:update",
  PROJECT_CREATE: "workspace:project:create",
  PROJECT_LIST: "workspace:project:list",
  API_KEY_LIST: "workspace:api_key:list",
  API_KEY_MANAGE: "workspace:api_key:manage",
  PROJECT_READ: "project:read",
  PROJECT_UPDATE: "project:update",
  PROJECT_SCRIPT_UPDATE: "project:script:update",
  PROJECT_SCRIPT_LOCK: "project:script:lock",
  PROJECT_EXPORT: "project:export",
  PROJECT_AI_RERUN: "project:budget:rerun",
} as const;

export type RbacPermissionKey = (typeof P)[keyof typeof P];

export type RbacMatrixRow = {
  resource: string;
  view: RbacPermissionKey | null;
  actions: RbacPermissionKey | null;
  control: RbacPermissionKey | null;
};

export const RBAC_MATRIX_ROWS: RbacMatrixRow[] = [
  {
    resource: "Workspace",
    view: P.WORKSPACE_READ,
    actions: null,
    control: P.WORKSPACE_UPDATE,
  },
  {
    resource: "Members",
    view: P.MEMBER_LIST,
    actions: P.MEMBER_INVITE,
    control: P.MEMBER_UPDATE,
  },
  {
    resource: "Billing",
    view: P.BILLING_READ,
    actions: P.BILLING_UPDATE,
    control: null,
  },
  {
    resource: "Projects",
    view: P.PROJECT_LIST,
    actions: P.PROJECT_CREATE,
    control: null,
  },
  {
    resource: "API keys",
    view: P.API_KEY_LIST,
    actions: null,
    control: P.API_KEY_MANAGE,
  },
  {
    resource: "Assigned projects",
    view: P.PROJECT_READ,
    actions: P.PROJECT_UPDATE,
    control: null,
  },
  {
    resource: "Script",
    view: P.PROJECT_READ,
    actions: P.PROJECT_SCRIPT_UPDATE,
    control: P.PROJECT_SCRIPT_LOCK,
  },
  {
    resource: "Exports",
    view: null,
    actions: P.PROJECT_EXPORT,
    control: null,
  },
  {
    resource: "AI runs",
    view: null,
    actions: P.PROJECT_AI_RERUN,
    control: null,
  },
];

const PROJECT_CREATOR_PERMS = [
  P.PROJECT_READ,
  P.PROJECT_UPDATE,
  P.PROJECT_SCRIPT_UPDATE,
  P.PROJECT_SCRIPT_LOCK,
  P.PROJECT_EXPORT,
  P.PROJECT_AI_RERUN,
] as const;

const PROJECT_EDITOR_PERMS = [
  P.PROJECT_READ,
  P.PROJECT_SCRIPT_UPDATE,
  P.PROJECT_EXPORT,
  P.PROJECT_AI_RERUN,
] as const;

/** Aligned with workspace + project role bundles for marketing display. */
export const RBAC_ROLE_PERMISSIONS: Record<
  MarketingWorkspaceRole,
  readonly RbacPermissionKey[]
> = {
  Administrator: [
    P.WORKSPACE_READ,
    P.WORKSPACE_UPDATE,
    P.MEMBER_LIST,
    P.MEMBER_INVITE,
    P.MEMBER_UPDATE,
    P.MEMBER_REMOVE,
    P.BILLING_READ,
    P.BILLING_UPDATE,
    P.PROJECT_LIST,
    P.PROJECT_CREATE,
    P.API_KEY_LIST,
    P.API_KEY_MANAGE,
    ...PROJECT_CREATOR_PERMS,
  ],
  Producer: [
    P.WORKSPACE_READ,
    P.WORKSPACE_UPDATE,
    P.MEMBER_LIST,
    P.MEMBER_INVITE,
    P.MEMBER_UPDATE,
    P.MEMBER_REMOVE,
    P.PROJECT_LIST,
    P.PROJECT_CREATE,
    P.API_KEY_LIST,
    P.API_KEY_MANAGE,
    ...PROJECT_CREATOR_PERMS,
  ],
  Editor: [P.WORKSPACE_READ, P.API_KEY_LIST, ...PROJECT_EDITOR_PERMS],
  Viewer: [P.WORKSPACE_READ, P.PROJECT_READ],
};

export const rbacPermissionCount = (role: MarketingWorkspaceRole): number =>
  RBAC_ROLE_PERMISSIONS[role].length;

export const rbacRoleHasPermission = (
  role: MarketingWorkspaceRole,
  key: RbacPermissionKey,
): boolean => RBAC_ROLE_PERMISSIONS[role].includes(key);

export const getRowPermission = (
  row: RbacMatrixRow,
  column: RbacActionColumn,
): RbacPermissionKey | null => {
  if (column === "View") return row.view;
  if (column === "Actions") return row.actions;
  return row.control;
};
